import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { SPOTIFY_FOR_ARTISTS_CONNECT_PATH } from '../../../mobileApps/mobileAppLinks';
import { useArtistPortalConnect } from '../../../smartLinks/catalog/useArtistPortalConnect';
import { ConnectOutcome, connectOutcome } from '../../../smartLinks/catalog/connectOutcome';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';

/** The router path of the Catalog connect of Spotify for Artists (no `/app`). */
const CATALOG_CONNECT_PATH = SPOTIFY_FOR_ARTISTS_CONNECT_PATH.replace(/^\/app/, '');

/**
 * "Connect Spotify for Artists" and "Reconnect" of the Canvas row (design spotify-canvas a2 note):
 * the Spotify for Artists sign-in of Catalog. On the web it runs in place (the same sign-in window
 * as the Catalog connect sheet), so the artist stays on this page. When the login holds several
 * artists or none, and inside the native app (which runs its own connect), the Catalog connect
 * sheet takes over (`/catalog/connect/spotify-artists`).
 */
export function useSpotifyCanvasConnect({ accountId, onConnected }: { accountId: number; onConnected: () => void }) {
  const portal = useArtistPortalConnect({ accountId });
  const navigate = useNavigate();
  const isNativeEmbed = useIsNativeEmbed();
  const { connectSpotify } = portal;

  const openCatalogConnect = useCallback(() => {
    // eslint-disable-next-line no-void
    void navigate(CATALOG_CONNECT_PATH);
  }, [navigate]);

  const connect = useCallback(() => {
    if (isNativeEmbed) {
      openCatalogConnect();
      return;
    }
    connectSpotify(MusicAPIIntegrationID.Spotify)
      .then((result) => {
        if (result === null) {
          // Closed by the artist, or refused: `portal.error` holds the refusal.
          return;
        }
        if (connectOutcome(result) === ConnectOutcome.done) {
          onConnected();
          return;
        }
        openCatalogConnect();
      })
      .catch(console.error);
  }, [connectSpotify, isNativeEmbed, onConnected, openCatalogConnect]);

  return { connect, isConnecting: portal.isBusy, error: portal.error };
}
